import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, img, inr, errMsg, useSEO } from "../../lib/api";
import { useShop } from "../../context/ShopContext";
import { useAuth } from "../../context/AuthContext";
import { useQuote, SummaryRows, CouponBox } from "../../components/store/CartDrawer";

export const STATES = ["Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"];
const FIELDS = [["name", "Full name", "text"], ["mobile", "Mobile number", "tel"], ["email", "Email", "email"], ["house", "House / Flat no.", "text"], ["street", "Street / Locality", "text"], ["city", "City", "text"], ["pincode", "PIN code", "text"]];

const loadRzp = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

export default function Checkout() {
  const { cart, coupon, clearCart, settings } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState({ name: "", mobile: "", email: "", house: "", street: "", city: "", state: "", pincode: "", instructions: "" });
  const [saved, setSaved] = useState([]);
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const q = useQuote({ state: a.state || null, mobile: a.mobile || null, email: a.email || null });
  useSEO({ title: "Checkout" });
  const online = settings.payments_online_enabled;
  const cod = settings.payments?.cod_enabled !== false;

  useEffect(() => {
    setMethod(online ? "razorpay" : cod ? "cod" : "");
  }, [online, cod]);
  useEffect(() => {
    if (user) {
      setA((x) => ({ ...x, name: x.name || user.name || "", email: x.email || user.email || "", mobile: x.mobile || user.mobile || "" }));
      api.get("/account/addresses").then((r) => setSaved(r.data)).catch(() => {});
    }
  }, [user]);

  const validate = () => {
    const e = {};
    FIELDS.forEach(([k]) => { if (!a[k].trim()) e[k] = "Required"; });
    if (a.mobile && !/^[6-9]\d{9}$/.test(a.mobile.replace(/\D/g, "").slice(-10))) e.mobile = "Enter a valid 10-digit mobile";
    if (a.pincode && !/^[1-9]\d{5}$/.test(a.pincode)) e.pincode = "Enter a valid 6-digit PIN";
    if (a.email && !/^\S+@\S+\.\S+$/.test(a.email)) e.email = "Enter a valid email";
    if (!a.state) e.state = "Select state";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const place = async () => {
    if (!validate()) return toast.error("Please fix the highlighted fields");
    if (!method) return toast.error("No payment method is available right now");
    setBusy(true);
    try {
      const items = cart.map(({ product_id, variant_id, qty }) => ({ product_id, variant_id, qty }));
      const { data } = await api.post("/checkout", { items, coupon_code: q?.coupon_code || null, address: a, payment_method: method, lead_session_id: localStorage.getItem("seed_chat_sid") });
      toast.dismiss();
      if (method === "cod") {
        clearCart();
        return navigate(`/order/${data.order_id}`);
      }
      if (!(await loadRzp())) throw new Error("Could not load the payment window. Check your connection.");
      const rz = new window.Razorpay({
        key: data.razorpay_key_id, amount: data.amount, currency: "INR", order_id: data.razorpay_order_id, name: "SEED COSMETICS",
        description: `Order ${data.order_number}`, prefill: { name: a.name, email: a.email, contact: a.mobile }, theme: { color: "#1B3B2B" },
        handler: async (r) => {
          try {
            await api.post("/payments/razorpay/verify", { order_id: data.order_id, ...r });
            clearCart();
            navigate(`/order/${data.order_id}`);
          } catch (er) {
            toast.error("We could not verify the payment. If money was deducted, it will be confirmed shortly.");
            navigate(`/order/${data.order_id}`);
          }
        },
        modal: { ondismiss: () => { setBusy(false); toast("Payment not completed. Your order is saved as pending."); } },
      });
      rz.on("payment.failed", (r) => api.post("/payments/razorpay/failed", { order_id: data.order_id, reason: r.error?.description || "" }));
      rz.open();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (!cart.length) return <div className="container-x py-24"><h1 className="text-4xl text-[#1B3B2B]">Your cart is empty</h1><Link to="/shop" className="underline mt-4 inline-block" data-testid="checkout-shop-link">Continue shopping</Link></div>;
  const inp = (k) => `w-full border rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-[#6B8E78] ${errors[k] ? "border-[#B91C1C]" : "border-[#E8E0D5]"}`;

  return (
    <div className="container-x py-10 grid lg:grid-cols-[1fr_400px] gap-10">
      <div className="space-y-8">
        <div><h1 className="text-4xl sm:text-5xl text-[#1B3B2B] tracking-tight">Checkout</h1>
          <p className="text-sm text-stone-500 mt-2">{user ? `Signed in as ${user.email}` : <>Checking out as guest. <Link to="/login?next=/checkout" className="underline" data-testid="checkout-login-link">Log in</Link> for faster checkout (optional).</>}</p></div>
        {saved.length > 0 && <div className="flex gap-2 flex-wrap">{saved.map((s) => <button key={s.id} onClick={() => setA({ ...a, ...s })} className="text-xs border border-[#E8E0D5] rounded-full px-3 py-1.5" data-testid="saved-address-button">{s.house}, {s.city}</button>)}</div>}
        <section className="space-y-4" data-testid="checkout-address-form">
          <h2 className="text-2xl text-[#1B3B2B]">Contact & delivery</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, l, t]) => (
              <div key={k} className={k === "street" ? "sm:col-span-2" : ""}>
                <label className="text-xs text-stone-500">{l}</label>
                <input type={t} value={a[k]} onChange={(e) => setA({ ...a, [k]: e.target.value })} className={inp(k)} data-testid={`checkout-${k}-input`} inputMode={k === "pincode" || k === "mobile" ? "numeric" : undefined} />
                {errors[k] && <p className="text-xs text-[#B91C1C] mt-1">{errors[k]}</p>}
              </div>
            ))}
            <div><label className="text-xs text-stone-500">State</label>
              <select value={a.state} onChange={(e) => setA({ ...a, state: e.target.value })} className={inp("state")} data-testid="checkout-state-select"><option value="">Select state</option>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
              {errors.state && <p className="text-xs text-[#B91C1C] mt-1">{errors.state}</p>}</div>
            <div className="sm:col-span-2"><label className="text-xs text-stone-500">Delivery instructions (optional)</label><textarea rows={2} value={a.instructions} onChange={(e) => setA({ ...a, instructions: e.target.value })} className={inp("x")} data-testid="checkout-instructions-input" /></div>
          </div>
        </section>
        <section className="space-y-3" data-testid="payment-methods">
          <h2 className="text-2xl text-[#1B3B2B]">Payment method</h2>
          <label className={`flex items-start gap-3 border rounded-xl p-4 ${online ? "border-[#E8E0D5] bg-white cursor-pointer" : "opacity-60 border-dashed"}`}>
            <input type="radio" disabled={!online} checked={method === "razorpay"} onChange={() => setMethod("razorpay")} data-testid="payment-razorpay" />
            <span><span className="font-medium text-sm">Pay online — UPI, Cards, Net Banking</span><span className="block text-xs text-stone-500">{online ? "Secure payment via Razorpay" : "Online payment is being set up and will be available soon"}</span></span>
          </label>
          {cod && <label className="flex items-start gap-3 border border-[#E8E0D5] bg-white rounded-xl p-4 cursor-pointer">
            <input type="radio" checked={method === "cod"} onChange={() => setMethod("cod")} data-testid="payment-cod" />
            <span><span className="font-medium text-sm">Cash on Delivery</span><span className="block text-xs text-stone-500">Pay when your order arrives</span></span>
          </label>}
        </section>
      </div>
      <aside className="lg:sticky lg:top-24 h-fit border border-[#E8E0D5] rounded-2xl p-5 sm:p-6 bg-[#FFFDF9] space-y-5">
        <h2 className="text-2xl text-[#1B3B2B]">Order summary</h2>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {(q?.lines || []).map((l) => (
            <div key={l.product_id + (l.variant_id || "")} className="flex gap-3 text-sm">
              <img src={img(l.image)} alt={l.name} className="w-14 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0"><p className="truncate">{l.name}{l.variant_name && ` · ${l.variant_name}`}</p><p className="text-xs text-stone-500">Qty {l.qty} × {inr(l.price)} {l.discount_percent > 0 && <span className="text-[#B91C1C]">({Math.round(l.discount_percent)}% off)</span>}</p></div>
              <span>{inr(l.line_total)}</span>
            </div>
          ))}
        </div>
        {q?.errors?.map((e) => <p key={e} className="text-xs text-[#B91C1C]">{e}</p>)}
        <CouponBox error={q?.coupon_error} />
        {q?.coupon_error && <p className="text-xs text-[#B91C1C]" data-testid="checkout-coupon-error">{q.coupon_error}</p>}
        {q && <SummaryRows q={q} />}
        <button disabled={busy || !q?.lines?.length} onClick={place} data-testid="place-order-button" className="w-full bg-[#1B3B2B] text-[#FBF8F3] rounded-full py-4 text-sm hover:bg-[#2C523A] disabled:opacity-50">
          {busy ? "Processing…" : method === "cod" ? `Place order · ${inr(q?.total)}` : `Pay ${inr(q?.total)}`}
        </button>
        <p className="text-[11px] text-stone-400 text-center">By placing the order you agree to our <Link to="/page/terms-and-conditions" className="underline">Terms</Link> and <Link to="/page/privacy-policy" className="underline">Privacy Policy</Link>.</p>
      </aside>
    </div>
  );
}
